import { HttpFailure } from './http-failure';

/**
 * 034 T016 — the HTTP surface, replacing Angular's `HttpClient`.
 *
 * Deliberately TINY: five verbs, JSON in and out, and a thrown `HttpFailure` on anything that is
 * not ok. Angular's client carried observables, interceptor plumbing, progress events and a
 * response-type union; none of that is used by this application except the interceptor, which is
 * reintroduced as an explicit wrapper rather than as ambient behaviour (see below).
 *
 * **WHY THE CLIENT IS A PARAMETER IN SOME PLACES.** Session traffic — and the preferences read
 * that happens inside a renewal — must go straight to the network, bypassing the renewing wrapper.
 * Otherwise those calls can themselves 401 and re-enter the renewal they are running inside:
 * through `renew()` they await their own in-flight promise and hang, and through `restore()` they
 * recurse. Angular expressed that as `new HttpClient(inject(HttpBackend))`; here it is simply
 * `directClient`, and the fact that a caller must ASK for it makes the bypass visible at the call
 * site instead of hidden in an injector's configuration.
 */
export interface HttpClient {
  get<T>(url: string, options?: RequestOptions): Promise<T>;
  post<T>(url: string, body: unknown, options?: RequestOptions): Promise<T>;
  put<T>(url: string, body: unknown, options?: RequestOptions): Promise<T>;
  patch<T>(url: string, body: unknown, options?: RequestOptions): Promise<T>;
  delete<T>(url: string, options?: RequestOptions): Promise<T>;
}

export interface RequestOptions {
  readonly headers?: Readonly<Record<string, string>>;
  /** `blob` for the CSV downloads (020); `toApiErrorFromBlob` reads the failure body back out. */
  readonly responseType?: 'json' | 'blob';
  /**
   * A request that passes this is aborted once `timeoutMs` has elapsed, and the
   * abort is reported exactly as any other request that never completed: `HttpFailure` status `0`,
   * the same shape a rejected `fetch` (a refused connection, DNS) already produces. Nothing
   * downstream learns a new outcome shape from a timeout — `isWaitingStatus` in `outbox.store.ts`
   * already treats status 0 as "try again", which is exactly right for a send that timed out.
   *
   * 036 (issue 208, FR-004, research R-006) — no longer unset everywhere but the outbox's own send.
   * `send()` below defaults this to `READ_TIMEOUT_MS` for a `GET` and `WRITE_TIMEOUT_MS` for every
   * other verb when a caller omits it; a caller that passes its own (session traffic's
   * `SESSION_TIMEOUT_MS`, the outbox's own send) keeps exactly that number instead.
   */
  readonly timeoutMs?: number;
  /**
   * 036 (issue 208, research R-008) — `core/stored-views/`'s ONE way to read the response `Date`
   * header without a second `fetch`. `send()` below calls this with the real `Response`'s headers
   * the moment they arrive (success or refusal alike), and this client still resolves or rejects
   * with the BODY only, exactly as it always has: nothing else may read `Response` or `Headers`
   * directly, and no other caller is given a reason to.
   */
  readonly onHeaders?: (headers: Headers) => void;
  /**
   * 036 (issue 208, research R-008, FR-022) — `core/stored-views/`'s storing client reads this to
   * decide whether a refusal may be papered over with a stored answer at all. `true` for a GET that
   * is CONFIRMING something just written (a re-read after a tick, a move, a capture) rather than
   * drawing a readable screen for the first time: `home.store.ts`'s own five-request `readHomePage()`
   * leaves this unset (a stored view is exactly what FR-021 wants there), while every re-read a write
   * triggers (`rereadAvailable`/`rereadDeferred`/`rereadCompletedRecent`, `quickAdd`'s own two) sets
   * it — a refused re-read must surface the existing "could not refresh itself" banner (FR-010's own
   * contract, `e2e/move-to-picker.spec.ts`'s "re-read fails" case), never silently show a list that
   * does not yet carry the write the practitioner just watched happen.
   */
  readonly requireLiveAnswer?: boolean;
}

/**
 * 036 (issue 208, FR-004) — a read left unanswered longer than this MUST be treated as one that
 * cannot presently be answered right now, and tried again rather than waited on. Three seconds,
 * not ten: a read blocks a screen the practitioner is looking at, where a write merely blocks a
 * capture or an edit already recorded on the device (FR-006) and behind an outbox that will try it
 * again regardless (FR-054).
 */
export const READ_TIMEOUT_MS = 3_000;

/**
 * 036 (issue 208, research R-006) — the default for every write that does not state its own. Ten
 * seconds: longer than a read's three, because a write cut short has to be sent again rather than
 * simply re-asked, and FR-054 is what makes doing that safe. `core/outbox/outbox.store.ts`'s own
 * send already used this exact number (`SEND_TIMEOUT_MS`) before this constant existed; it now
 * imports this one rather than restating the literal, so the number is written once.
 */
export const WRITE_TIMEOUT_MS = 10_000;

/**
 * A body the browser must serialise itself, not one this client may `JSON.stringify`.
 *
 * **Only `FormData` today, and the narrowness is the point.** 020's import is the one place in the
 * application that sends a file; `Blob` and `URLSearchParams` are not sent anywhere, so admitting
 * them here would be a capability with no caller and no test.
 */
const isRawBody = (body: unknown): body is FormData =>
  typeof FormData !== 'undefined' && body instanceof FormData;

async function send<T>(method: string, url: string, body: unknown, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { ...options.headers };
  const hasBody = body !== undefined;
  /**
   * **A multipart body is passed through, and NO `Content-Type` is set for it.** The boundary is
   * part of that header (`multipart/form-data; boundary=…`) and only `fetch` knows it, so a
   * hand-written header produces a request the server cannot parse. Angular's `HttpClient` did
   * exactly this detection internally, which is why `import.store.ts` never had to say so.
   *
   * **The port dropped it, and the failure was silent in the worst way.** This client's header
   * comment calls it "JSON in and out", and `JSON.stringify(new FormData())` is `'{}'` — so an
   * import POSTed a two-character JSON body with no file in it. The server answers a file it cannot
   * read with a **200** carrying zero tallies and one error (contracts/import-actions.md §File-Level
   * Refusals), so the page would have shown "0 rows read" and looked like a legitimately bad CSV
   * rather than a broken client. Found porting `/import`; verified with `JSON.stringify(FormData)`.
   */
  const raw = isRawBody(body);
  if (hasBody && !raw) headers['Content-Type'] = 'application/json';

  /**
   * An optional time limit, `AbortController`-backed. `http.ts` had none at
   * all, so one stalled request held whatever lock or UI state was waiting on it for as long as the
   * browser allowed — the outbox's own send lock, and the capture box behind it, in every window.
   *
   * `fetch()`'s own promise settles as soon as the status line and headers arrive; the BODY is a
   * separate stream a connection can stall on indefinitely afterward, and nothing else here counts
   * down against that — the exact hang this limit exists to prevent. The timer stays armed until
   * `readBody()` (below) has actually finished, on every path (ok, refused, and the 204 short
   * circuit, which reads nothing but still passes through the same `finally`).
   *
   * 036 (issue 208, FR-004): a caller that names no `timeoutMs` at all no longer waits forever —
   * `READ_TIMEOUT_MS` for a `GET`, `WRITE_TIMEOUT_MS` for anything else. A caller that DOES pass one
   * (session traffic's `SESSION_TIMEOUT_MS`, the outbox's own send) keeps exactly that number.
   */
  const timeoutMs = options.timeoutMs ?? (method === 'GET' ? READ_TIMEOUT_MS : WRITE_TIMEOUT_MS);
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    let response: Response;
    try {
      response = await fetch(url, {
        method,
        headers,
        body: hasBody ? (raw ? body : JSON.stringify(body)) : undefined,
        // The refresh token lives in localStorage, not a cookie, so credentials are never needed —
        // and omitting them keeps a cross-origin development setup from depending on CORS
        // credential negotiation it does not otherwise require.
        credentials: 'omit',
        signal: controller.signal,
      });
      // 036 (issue 208, research R-008): the ONE place a caller can read a response header without
      // a second `fetch`. Called before the ok/refused branch below so it fires for every response
      // this client receives, whatever its status — `core/stored-views/`'s storing client is the
      // one caller today, reading `Date` off a successful `GET`.
      options.onHeaders?.(response.headers);
    } catch (cause) {
      /**
       * `fetch` rejects only when the request never completed — DNS, a refused connection, an
       * abort. That is exactly Angular's status 0, which `toApiError` already maps to
       * `api.unreachable`, so the two agree without either restating the other's vocabulary — an
       * abort from `timeoutMs` above is deliberately not distinguished from any other reason
       * `fetch` never completed, headers-stage or body-stage alike.
       */
      throw new HttpFailure({ status: 0, error: cause, statusText: 'Network request failed', url });
    }

    if (!response.ok) {
      let errorBody: unknown;
      try {
        errorBody = await readBody(response, options.responseType);
      } catch (cause) {
        // The BODY stream itself failed to arrive (a stall the timer's own
        // abort just ended, or any other mid-body drop): "the request never completed" either way,
        // exactly `fetch()`'s own rejection above, not a refusal this response's status could name.
        throw new HttpFailure({ status: 0, error: cause, statusText: 'Network request failed', url });
      }
      throw new HttpFailure({
        status: response.status,
        error: errorBody,
        statusText: response.statusText,
        url: response.url,
      });
    }

    if (response.status === 204) return undefined as T;
    try {
      return (await readBody(response, options.responseType)) as T;
    } catch (cause) {
      // Same reasoning as the refusal branch above: a stall mid-body under an OK status is still
      // "the request never completed," not a successful answer with nothing in it.
      throw new HttpFailure({ status: 0, error: cause, statusText: 'Network request failed', url });
    }
  } finally {
    clearTimeout(timer);
  }
}

/**
 * A body that PARSES badly must never throw: a refusal that arrives with a body this client
 * cannot make JSON of is still a refusal, and losing it to a JSON error would replace the server's
 * status with a crash. A body that never ARRIVES at all is a different fact
 * (the stream itself rejecting, whether from a stall the send timer's own abort ended or any other
 * mid-body drop) and is deliberately left to propagate: `send()`'s own callers now turn that into
 * the same status-`0` "the request never completed" `HttpFailure` a rejected `fetch()` already
 * produces, rather than this function quietly reading it as an empty body.
 */
async function readBody(response: Response, responseType: RequestOptions['responseType']): Promise<unknown> {
  if (responseType === 'blob') return response.blob();
  const text = await response.text();
  if (text.length === 0) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

/**
 * The client that does NOT renew. Everything session-shaped uses this, and so does the preferences
 * read that runs inside a renewal.
 */
const realDirectClient: HttpClient = {
  get: (url, options) => send('GET', url, undefined, options),
  post: (url, body, options) => send('POST', url, body, options),
  put: (url, body, options) => send('PUT', url, body, options),
  patch: (url, body, options) => send('PATCH', url, body, options),
  delete: (url, options) => send('DELETE', url, undefined, options),
};

let direct: HttpClient = realDirectClient;

/**
 * A FUNCTION, not the constant it started as — and the session specs are what forced it.
 *
 * Angular injected `HttpBackend`, so a test could substitute what session traffic went through. A
 * module constant cannot be substituted, so the first port of these specs issued real `fetch` calls
 * and every `expectOne` found nothing. Porting dependency injection to module constants silently
 * loses substitutability; this restores it at the one seam that needs it.
 */
export const directClient = (): HttpClient => direct;

/** Test seam, paired with `setAppClient`. Production code never calls it. */
export function setDirectClient(client: HttpClient): void {
  direct = client;
}

/**
 * The client ordinary screens use: the one that renews on a 401.
 *
 * It starts as `directClient` and the session layer installs the renewing wrapper over it at
 * bootstrap — which is what Angular expressed by REGISTERING `authInterceptor` in `app.config.ts`.
 * A mutable binding rather than a parameter on every call because the alternative is threading a
 * client through every store and screen, which is the plumbing Angular's injector existed to hide
 * and which no screen should have an opinion about.
 *
 * The seam is narrow on purpose: `setAppClient` is called once, from the session layer, and a
 * test that wants a stub calls it too. Nothing else may.
 */
let installed: HttpClient = realDirectClient;

export const appClient = (): HttpClient => installed;

export function setAppClient(client: HttpClient): void {
  installed = client;
}
